import { ArchiveIcon, Crosshair2Icon, RocketIcon } from "@radix-ui/react-icons";
import { TabsContent } from "@radix-ui/react-tabs";
import moment from "moment";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import SaveSessionDrawer from "src/components/SaveSessionDrawer";
import UserSettings from "src/components/UserSettings";
import ActiveFriendsChips from "src/components/active-friends-chips/ActiveFriendsChips";
import { CurrentDrinkLog } from "src/components/drink-log/DrinkLog";
import EventSelector from "src/components/event-selector/EventSelector";
import PhotoSelector from "src/components/photo-selector/PhotoSelector";
import { Alert, AlertDescription, AlertTitle } from "src/components/ui/alert";
import { Button } from "src/components/ui/button";
import { Separator } from "src/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "src/components/ui/tabs";
import { calculateTimeToSober } from "src/lib/calculatePermille";
import getLogsFromAlcoholData from "src/lib/getLogsFromAlcoholData";
import usePermilleChartData from "src/lib/hooks/usePermilleChartData";
import Indicator from "../components/Indicator";
import { MapDrawer } from "../components/RouteMap";
import DrinkSelector from "../components/drink-selector/DrinkSelector";
import style from "./Home.module.css";

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export default function Home() {
  const alcoholState = useSelector((state: any) => state.alcohol);

  const user = useSelector((state: any) => state.user);

  const permilleChartData = usePermilleChartData({
    DrunkDrinks: alcoholState.DrunkDrinks,
    user: user,
    includeCurrentPermille: true,
    autocut: false,
  });

  const lastLogTime = useMemo(() => {
    const logs = getLogsFromAlcoholData(alcoholState);
    if (!logs.length) return null;

    return Date.now() - logs[0].data.date;
  }, [alcoholState]);

  const lastLogMoreThanDayAgo = lastLogTime
    ? lastLogTime > 1000 * 60 * 60 * 24
    : false;

  const soberDate = useMemo(() => {
    const timeToSober = calculateTimeToSober({
      DrunkDrinks: alcoholState.DrunkDrinks,
      user,
    });
    if (!timeToSober) return null;
    return new Date(Date.now() + timeToSober);
  }, [alcoholState.DrunkDrinks, user]);

  return (
    <>
      {/* <SmallPartyCard className="px-4 py-2" /> */}
      <ActiveFriendsChips
        style={{
          padding: "0.5rem 1rem",
        }}
      />
      {(!user.weight || !user.gender) && (
        <div className="px-4 py-2">
          <Alert>
            <RocketIcon className="h-4 w-4" />
            <AlertTitle>Nastav si pohlaví a váhu</AlertTitle>
            <AlertDescription>
              Pro lepší výpočet promile je potřeba znát tvoji váhu a pohlaví.
              <UserSettings asChild>
                <Button className="block mt-2">Nastavení uživatele</Button>
              </UserSettings>
            </AlertDescription>
          </Alert>
        </div>
      )}
      <div
        style={{
          height: "40vh",
          display: "flex",
          justifyContent: "center",
          padding: "0.5rem",
        }}
      >
        <Indicator
          value={alcoholState.permille}
          maxValue={alcoholState.sessionMaxPermille}
        />
      </div>
      <div className="justify-center flex items-center gap-2 pb-4">
        <div className="h-9 w-9">
          <img src="/car.svg" alt="" />
        </div>
        {soberDate ? (
          <div>
            <span className="font-bold block" style={{ lineHeight: "1.2rem" }}>
              {capitalize(moment(soberDate).locale("cs").calendar())}
            </span>
            <span
              className="text-sm text-muted-foreground block"
              style={{ lineHeight: "1rem" }}
            >
              budeš střízlivý
            </span>
          </div>
        ) : (
          <span className="font-bold block">Jsi střízlivý</span>
        )}
      </div>
      <div className="py-2">
        <Tabs defaultValue="drink">
          <TabsList
            className={`rounded-md ${style.tabs}`}
            style={{
              backgroundColor: "hsl(var(--muted))",
              display: "flex",
              flexWrap: "wrap",
              rowGap: "0.25rem",
              height: "fit-content",
              top: 0,
              margin: "0 0.5rem",
            }}
          >
            <TabsTrigger value="drink">Přidat nápoj</TabsTrigger>
            <TabsTrigger value="event">Přidat událost</TabsTrigger>
            <TabsTrigger value="photo">Přidat fotku</TabsTrigger>
          </TabsList>
          <TabsContent value="drink" className="pt-2">
            <DrinkSelector />
          </TabsContent>
          <TabsContent value="event" className="pt-2">
            <EventSelector />
          </TabsContent>
          <TabsContent value="photo" className="pt-2">
            <PhotoSelector />
          </TabsContent>
        </Tabs>
      </div>
      <div className="p-4">
        <Separator />
      </div>
      <div>
        <div className="flex items-center justify-center relative">
          <h2 className="text-lg font-bold text-center">Záznamník</h2>
          <SaveSessionDrawer>
            <Button
              size="icon"
              className={`absolute right-4 ${
                lastLogMoreThanDayAgo ? "animate-bounce" : ""
              }`}
              disabled={!getLogsFromAlcoholData(alcoholState).length}
            >
              <ArchiveIcon />
            </Button>
          </SaveSessionDrawer>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "0.5rem",
            padding: "0.5rem 0",
          }}
        >
          {/* <StatsDrawer
            chartData={permilleChartData}
            permille={alcoholState.permille}
            maxPermille={alcoholState.sessionMaxPermille}
            asChild
          >
            <Button
              variant="outline"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <BarChartIcon />
              Statistiky
            </Button>
          </StatsDrawer> */}
          <MapDrawer selectOwnLogs selectOwnRoute asChild>
            <Button
              variant="outline"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Crosshair2Icon />
              Mapa
            </Button>
          </MapDrawer>
        </div>
        <div className="px-4 pt-2 pb-4">
          <CurrentDrinkLog />
        </div>
      </div>
    </>
  );
}
